import { inject, Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { KeycloakLoginOptions, KeycloakOnLoad } from 'keycloak-js/lib/keycloak';
import { isEmpty } from 'lodash';
import { Nullable } from 'primeng/ts-helpers';
import { BehaviorSubject, filter } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserProfile } from '../../shared/contract/user-profile.contract';
import { User } from '../../shared/contract/user.contract';
import { Authorities } from '../../shared/enums/authorities.enum';
import { Modules } from '../../shared/enums/modules.enum';
import { PrivilegedRole } from '../../shared/enums/privileged-roles.enum';
import { SENTRY_TOKEN, WINDOW_TOKEN } from '../tokens/app-tokens';
import { AuthLifecycleStorageService } from './auth-lifecycle-storage.service';
import { AuthRealmService } from './auth-realm.service';
import { LogoutService } from './logout.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private keycloakService = inject(KeycloakService);
  private logoutService = inject(LogoutService);
  private authLifecycleStorageService = inject(AuthLifecycleStorageService);
  private window = inject(WINDOW_TOKEN);
  private sentry = inject(SENTRY_TOKEN);

  private idpLoginPath = '/external-login';
  private idpLoginParam = 'idp';
  private initOptionOnLoad = <KeycloakOnLoad>(environment?.keycloak?.initOptions?.onLoad || 'check-sso');

  private _userProfile$ = new BehaviorSubject<UserProfile>({});
  private _token$ = new BehaviorSubject<string>('');
  private _user$ = new BehaviorSubject<User>(null as unknown as User);

  readonly userProfile$ = this._userProfile$.asObservable();
  readonly token$ = this._token$.asObservable();
  readonly user$ = this._user$.asObservable().pipe(filter(Boolean));

  get token(): string {
    return this._token$.getValue();
  }

  get user(): User {
    return this._user$.getValue();
  }

  get userCustomerId(): string {
    return this._user$.getValue().customerId;
  }

  constructor() {
    this._token$.pipe(filter(Boolean)).subscribe(() => {
      const user = this.getUserFromTokenParsed();
      if (user) {
        this._user$.next(user);
      }
    });
  }

  private getUserFromTokenParsed(): User | Nullable {
    const tokenParsed = this.keycloakService.getKeycloakInstance().tokenParsed;
    return tokenParsed ? new User(tokenParsed) : null;
  }

  init(): void {
    this.keycloakService
      .init({
        config: {
          url: `${AuthRealmService.getAuthBaseUrl()}/auth`,
          realm: `${AuthRealmService.getRealm()}`,
          clientId: environment.APP_CLIENT_ID,
        },
        initOptions: {
          onLoad: this.initOptionOnLoad,
        },
      })
      .then((authenticated) => {
        this.authLifecycleStorageService.updateAuthState(authenticated);
        if (!authenticated) {
          const idp = this.checkAndResolveIdpAlias();
          const loginParams: KeycloakLoginOptions | undefined = idp
            ? { redirectUri: window.location.origin, idpHint: idp }
            : undefined;
          this.keycloakService.login(loginParams).then(() => this.setSentryUser());
        } else {
          this.getToken();
        }
      });
  }

  private setSentryUser(): void {
    this.sentry.setUser({
      email: this.user.email,
      customerId: this.user.customerId,
      roles: this.user.roles?.join(','),
    });
  }

  logout(): void {
    this.logoutService.logout();
  }

  getToken(): void {
    if (this.keycloakService.isLoggedIn()) {
      this.keycloakService.getToken().then((data) => {
        this._token$.next(data);
        if (!isEmpty(this.user)) {
          this.setSentryUser();
        }
      });
    }
  }

  getUserProfile(): void {
    if (this.keycloakService.isLoggedIn()) {
      this.keycloakService.loadUserProfile().then((data) => this._userProfile$.next(data));
    }
  }

  isTokenExpired(): boolean {
    return this.keycloakService.isTokenExpired();
  }

  async updateToken(minValidity?: number): Promise<string> {
    return this.keycloakService.updateToken(minValidity).then(() => {
      const token = this.keycloakService.getKeycloakInstance().token || '';
      this._token$.next(token);
      return token;
    });
  }

  isInRole(role: PrivilegedRole): boolean {
    return (this.user.roles || []).includes(role);
  }

  isInRoles(rolesToCheck: PrivilegedRole[]): boolean {
    return rolesToCheck.some((role) => this.isInRole(role));
  }

  hasModule(module: Modules): boolean {
    if (this.isInRoles([PrivilegedRole.Superadmin, PrivilegedRole.Partneradmin])) {
      return true;
    }
    return (this.user.modules || []).includes(module);
  }

  hasModules(modules: Modules[]): boolean {
    return (modules ?? []).every((m) => this.hasModule(m));
  }

  hasAuthority(auth: Authorities): boolean {
    return (this.user.authorities || []).includes(auth);
  }

  hasAuthorities(authoritiesToCheck: Authorities[]): boolean {
    return authoritiesToCheck.every((authority) => this.hasAuthority(authority));
  }

  hasAnyAuthority(authoritiesToCheck: Authorities[]): boolean {
    return authoritiesToCheck.some((authority) => this.hasAuthority(authority));
  }

  private checkAndResolveIdpAlias(): string | null {
    const pathname = this.window.location.pathname;
    const search = this.window.location.search;

    if (pathname === this.idpLoginPath && search) {
      return new URLSearchParams(search).get(this.idpLoginParam);
    }

    return null;
  }
}
